import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { StepperComponent } from '~/components/Stepper/Stepper.component';
import { OrderDetailsComponent } from '~/containers/OrderDetails/OrderDetails.component';
import { PaymentMethodSelection } from '~/containers/PaymentMethodSelection/PaymentMethodSelection.component';
import { MeansOfPayment } from '~/entities/schemas/meansOfPayment';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { SessionsService } from '~/services/SessionsService';
import { OrderConfirmationAltUIComponent } from '~/containers/OrderConfirmationAltUI/OrderConfirmationAltUI.component';
import { AvailableCountry } from '~/entities/schemas/availableCountry';
import { DataTransacton } from '~/entities/schemas/dataTransaction';
interface CheckoutPageProps {
  handleSubmit: (
    values: any,
    payment?: MeansOfPayment,
    specificType?: string,
    selectedCountry?: AvailableCountry,
  ) => void;
  selectedItem?: number;
  paymentInfos: GetPaymentResponse;
  loading?: boolean;
  processingMobilePayment?: boolean;
  handleSubmitMobilePaymentForm: (values: any, payment: MeansOfPayment) => void;
  dataTransaction: DataTransacton | null;
  loadingDataTr: boolean;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({
  handleSubmit,
  selectedItem,
  loading,
  paymentInfos,
  processingMobilePayment,
  handleSubmitMobilePaymentForm,
  dataTransaction,
  loadingDataTr,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const setActiveIndex = (value: number) => setActiveStep(value);
  const [disabledOrderDetailsPage, setDisabledOrderDetailsPage] = useState<boolean>(false);
  const [disabledOrderConfirmationPage, setDisabledOrderConfirmationPage] = useState<boolean>(false);

  const urlMethod = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('method') ?? null;
  }, [search]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [showMethodSelection, setShowMethodSelection] = useState<boolean>(() => !urlMethod);

  useEffect(() => {
    if (urlMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(urlMethod);
      setShowMethodSelection(false);
    }
  }, [urlMethod, selectedPaymentMethod]);

  const handleSelectPaymentMethod = useCallback((specificType: string) => {
    setSelectedPaymentMethod(specificType);
    setShowMethodSelection(false);
  }, []);

  const onBackToMethodSelection = useCallback(() => {
    setShowMethodSelection(true);
    setSelectedPaymentMethod(null);
  }, []);

  const handleNavigateToConfirmation = useCallback(() => {
    setShowMethodSelection(false);
    setActiveIndex(1);
    setDisabledOrderDetailsPage(true);
    setDisabledOrderConfirmationPage(false);
  }, []);

  const stepperItems = useMemo(
    () => [
      { title: t('checkoutPage:orderDetails'), disabled: disabledOrderDetailsPage },
      { title: t('checkoutPage:orderConfirmation'), disabled: disabledOrderConfirmationPage },
    ],
    [t, disabledOrderDetailsPage, disabledOrderConfirmationPage],
  );

  // Handle Rendered View
  const stepperContent: ReactNode = useMemo(() => {
    const map: Record<number, ReactNode> = {
      0: (
        <OrderDetailsComponent
          handleSubmit={handleSubmit}
          paymentInfos={paymentInfos}
          loading={loading}
          processingMobilePayment={processingMobilePayment}
          handleSubmitMobilePaymentForm={handleSubmitMobilePaymentForm}
          dataTransaction={dataTransaction}
          loadingDataTr={loadingDataTr}
          initialPaymentMethod={selectedPaymentMethod ?? urlMethod ?? undefined}
          onBackToMethodSelection={onBackToMethodSelection}
          onNavigateToConfirmation={handleNavigateToConfirmation}
        />
      ),
      1: (
        <OrderConfirmationAltUIComponent
          loading={loading}
          selectedItem={selectedItem}
          paymentInfos={paymentInfos}
          dataTransaction={dataTransaction}
          loadingDataTr={loadingDataTr}
        />
      ),
    };
    return map[activeStep] || null;
  }, [
    activeStep,
    paymentInfos,
    loading,
    selectedItem,
    loadingDataTr,
    dataTransaction,
    showMethodSelection,
    urlMethod,
    onBackToMethodSelection,
    handleNavigateToConfirmation,
    handleSubmit,
    processingMobilePayment,
    handleSubmitMobilePaymentForm,
  ]);

  const handleChangeActivePage = useCallback((idx: number) => {
    setActiveIndex(idx);
  }, []);

  const handleInitPagesAccess = useCallback(() => {
    if (SessionsService.isAuthorizedToSuccessPage()) {
      handleChangeActivePage(1);
      setDisabledOrderDetailsPage(true);
      setDisabledOrderConfirmationPage(false);
    } else {
      setDisabledOrderDetailsPage(false);
      setDisabledOrderConfirmationPage(true);
    }
  }, [handleChangeActivePage]);

  useEffect(() => {
    handleInitPagesAccess();
  }, [handleInitPagesAccess]);

  // Si paiement en cours/terminé (transactionDetails), aller directement à OrderConfirmationAltUI
  if (SessionsService.isAuthorizedToSuccessPage()) {
    if (!paymentInfos) return null;
    return (
      <>
        <StepperComponent items={stepperItems} activeIndex={1} setActiveIndex={setActiveIndex} />
        <OrderConfirmationAltUIComponent
          loading={loading}
          selectedItem={selectedItem}
          paymentInfos={paymentInfos}
          dataTransaction={dataTransaction}
          loadingDataTr={loadingDataTr}
        />
      </>
    );
  }

  if (showMethodSelection) {
    if (!paymentInfos) return null;
    return <PaymentMethodSelection paymentInfos={paymentInfos} onSelect={handleSelectPaymentMethod} />;
  }

  return (
    <>
      <StepperComponent items={stepperItems} activeIndex={activeStep} setActiveIndex={setActiveIndex} />
      {stepperContent}
    </>
  );
};

export default CheckoutPage;
